import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

// Get Roles
export const getRoles = createAsyncThunk(
  "Roles/getRoles",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);

      const url = `${BASE_URL}/roles${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to get roles!";
      return rejectWithValue(message);
    }
  },
);

// Get Role Details
export const getRoleDetails = createAsyncThunk(
  "Roles/getRoleDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${BASE_URL}/roles/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get role details!";
      return rejectWithValue(message);
    }
  },
);

// Create Role
export const createRole = createAsyncThunk(
  "Roles/createRole",
  async (roleData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(`${BASE_URL}/roles`, roleData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message = error.response?.data?.message || "Failed to create role!";
      return rejectWithValue(message);
    }
  },
);

// Update Role
export const updateRole = createAsyncThunk(
  "Roles/updateRole",
  async ({ id, roleData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.put(`${BASE_URL}/roles/${id}`, roleData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message = error.response?.data?.message || "Failed to update role!";
      return rejectWithValue(message);
    }
  },
);

// Delete Role
export const deleteRole = createAsyncThunk(
  "Roles/deleteRole",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.delete(`${BASE_URL}/roles/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete role!";
      return rejectWithValue(message);
    }
  },
);

// Assign Permissions to Role
export const assignPermissionsToRole = createAsyncThunk(
  "Roles/assignPermissionsToRole",
  async ({ id, permissions }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/roles/${id}/permissions`,
        { permissions },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to assign permissions!";
      return rejectWithValue(message);
    }
  },
);
